/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace variable-name align */

import {
  catchError,
  first,
  tap
}                                       from 'rxjs/operators';

import {
  forkJoin,
  Observable,
  of,
  Subscriber
}                                       from 'rxjs';

import {
  HttpEvent,
  HttpEventType
}                                       from "@angular/common/http";

import {ProjectElement}                 from './project.element';
import {ProjectElementType}             from './project.element.type';

import {Project}                        from '../project';
import {ProjectType}                    from '../project.type';

import {
  Blob as FileBlob,
  Blob, BlobType
}                                       from '../blob';

import {Logger}                         from '@/util/logger';
import {ProjectElementFactory}          from '../project.element.factory';

import {
  basename,
  bytesToSize,
  errorAsText, fromMySQLDate,
  isset
} from '@/util/util';

import {TaskUpdateFileUploadFailed}     from "@/models/task.update.file.upload.failed";

import {TaskUpdateFileUpload}           from "@/models/task.update.file.upload";
import {TaskUpdateFileUploadComplete}   from "@/models/task.update.file.upload.complete";
import {TaskUpdate}                     from "@/models/task.update";
import {ProjectElementFactoryDecorator} from "@/models/project/project.element.factory.decorator";
import {ContentAwareTyping}             from "@/helpers/content.aware.typing";
import {BlobElement}                    from "@/models/project/blob.element";
import {SimConfig}                      from "@/models/sim/sim.config";
import {SimpleSimJob}                   from "@/models/sim/simple.sim.job";
import {
  SimulationJob,
  SimulationJobType,
  SimulationStatusType
} from "@/models/sim/simulation.job";
import {LineSimJobElement}              from "@/models/project/line.sim.job.element";
import {MovementMetaBuilder}            from "@/models/scenario/movement.meta.builder";
import {JSONLoader}                     from "@/loader/j.s.o.n.loader";
import {LoadingProgressEvent}           from '@/loader/loader.events';
import {RemoteSimJob}                   from "@/models/sim/remote.sim.job";
import {PropertyElement} from "@/models/project/property.element";
import {KPIXElement} from "@/models/project/k.p.i.x.element";
import {TaskSimImport} from "@/models/task.sim.import";
import {TaskSimImportFailed} from "@/models/task.sim.import.failed";
import {TaskSimImportComplete} from "@/models/task.sim.import.complete";

/**
 *
 * @class InputsElement
 *
 * @brief container for all the inputs in a simulation
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      April 2020
 *
 */
@ProjectElementFactoryDecorator('InputsElement')
export class InputsElement extends ProjectElement {

  protected actionKeys: string[] = [
    'newemptyinput',
    'adduploadinput',
    'addblobinput',
    'generatekpitable'
  ];

  /** when upload is in progress, this is non-empty */
  protected   pending: [File, string][]     = [];

  protected pendingKeys: [string, string][] = [];

  /** standard constructor */
  constructor(
     project: Project,
          id: string,
      parent: ProjectElement,
    publish?: boolean,
     peType?: ProjectElementType
  ) {

    super(project, id, peType ? peType : ProjectElementType.Inputs);

    this._acceptsElements.push(ProjectElementType.Blob, ProjectElementType.Dimension);

    this._enabled     = true;
    this._label       = 'Inputs';
    this._icon        = 'fas fa-arrow-right';
    this._elementKind = 'Inputs';

    if (parent) {

      this._project.addElement(this, parent, publish);
    }

    /* batch projects can  have dimensional input for creating N x N simulations */

    if (this._project.type === ProjectType.BatchSimulation) {

      this.actionKeys.push('dimensioninput');
    }


  }

  /** helper to use to for static global constructors (and factory registration) */
  static requireType() {
    console.log("requiring InputsElement");
  }

  /**
   * subclasses must add their menu item definition(s) to the given action set,
   * in jquery-contextmenu fashion.
   *
   * @param actionSet any
   *
   */
  public actions(actionSet: any): any {

    if (this._project.type === ProjectType.BatchSimulation) {

      actionSet.dimensioninput = {
        name: 'New Dimension',
        icon: 'fas fa-th-list',
        disabled: false
      };
    }

    actionSet.newemptyinput = {
      name: 'New (Empty)',
      icon: 'far fa-file',
      disabled: false
    };

    actionSet.addblobinput = {
      name: 'Add (Clone)',
      icon: 'fas fa-plus',
      disabled: false
    };

    actionSet.adduploadinput = {
      name: 'Add (Upload)',
      icon: 'fas fa-file-upload',
      disabled: this.pending.length !== 0
    };

    actionSet.generatekpitable = {
      name: 'Generate KPI Table',
      icon: 'far fa-chart-bar',
      disabled:  this.isBusy()
    };

    return actionSet;
  }

  /** can this element be a drop site of drag and drop? */
  public acceptsDrop(data): boolean {

    return true;
  }

  /** drop data on this element */
  public acceptDrop(data): boolean {

    return this.doDrop(data, this);
  }

  /**
   * allow users to import a remote simulation job into the project manager, so its like they
   * ran the simulation from the project manager in the first place.
   *
   * @param job the remote simulation to import.
   *
   */
  public importRemoteSimulation(job: SimpleSimJob): void {

    Logger.debug(`[InputsElement] importRemoteSimulation - importing remote job: `, job);

    /*
     * to make the subtree of a scenario, with a job under it, we have to first find
     * a name to use for the scenario; there might already be a scenario peer of the
     * same name.  That is, if you drag and drop a remote job twice, and it's the same
     * job both times, then we create a duplicate, we do not overwrite the existing one.
     * We can't overwrite, because we don't know if the first one came from an import
     * also, it might be something different, and just happens to have the same name.
     *
     * Once we have a scenario name to use, we can then fake creating a line simulator
     * job result child, and under that child we can add artifacts like KPI, KPIX, and
     * properties like reaction time.
     *
     * Note: May 5th, 2025 - we are dropping KPI and just keeping the KPIX artifact,
     * since it has everything a KPI has.  We still have to be backwards compatible so
     * we can't remove KPI support...but we want to now stop adding KPI elements to
     * the project tree. https://smartnetwork.atlassian.net/browse/SN-1751
     *
     * When we create the paths for the various artifacts, we also have to ensure that
     * they are the same structure as when we do a simulation acton directly in the
     * project manager panel.  This ensures that all the existing features continue
     * to work as if we had done the simulation "locally" in the project manager.
     *
     * For the actual importing of the artifacts, we use the googleImport() method
     * on the Blob service, which is similar to the create() method, but takes the
     * "content" from Google Bucket storage instead of from a File object or a string.
     * We batch that up and do a forkJoin to get that all rolling in parallel, and be as
     * fast as we can.
     *
     * Once all the artifacts have actually been imported...we can stamp out the subtree
     * nodes as needed, similar to how we create a fake job when we drop a scenario and
     * movement pair from the desktop into the project manager.  See the fakeResult()
     * method for that.
     *
     */

    /*
     * steps:
     *
     * 1. get an uuid - the output artifacts are filed in a unique sub-folder
     * 2. map google bucket artifacts to type and destination blob key
     * 3. batch (forkJoin) the blob imports
     * 4. create the subtree nodes as needed in the project manager
     *
     */

    let iel: InputsElement = this;
    let taskId: string     = TaskUpdate.generateId();
    let progress: number   = 0;

    iel.busy();

    let update: TaskSimImport = new TaskSimImport(taskId, `Importing ${job.jobId} via ${job.config.endpoint}...`, progress);
    iel._project.services.taskMonitor.progress(update);

    /* - - - - 1. get an uuid - the output artifacts are filed in a unique sub-folder  - - - - */

    Logger.debug(`[InputsElement] importRemoteSimulation - generating uuid ...`);

    iel._project.services.uuid.generate()
      .pipe(first())
      .subscribe({
        next: (uuidData: any): void => {

          /* - - - - 2. map google bucket artifacts to type and destination blob key  - - - - */

          /* set up the coordinates for the subtree ... */

          const uuid: string = uuidData.guid;
          const projectFolder: string = iel._project.dirname();
          const resultsFolder: string = projectFolder + `/results/${uuid}/`;

          const parents: ProjectElement[] = iel.ancestors().reverse();
          parents.shift();
          let storage: string = iel._project.dirname() +
            '/' +
            parents.map(x => x._label).join('/');

          Logger.debug(`[InputsElement] importRemoteSimulation - uuid=${uuid}, results=${resultsFolder}, inputFolder=${storage}`);

          /* hunt down the artifacts we need to import ... */

          let artifactMap: Map<string, string> = new Map<string, string>;

          if (typeof job.extra['artifact-scenario-download'] != 'undefined') {

            /* we have a scenario */

            artifactMap.set('scenario', job.extra['artifact-scenario-download']);
          }

          if (typeof job.extra['artifact-sim-cmd-download'] != 'undefined') {

            /* we have a scenario */

            artifactMap.set('cmd', job.extra['artifact-sim-cmd-download']);
          }

          if (typeof job.extra['artifact-sim-errors-download'] != 'undefined') {

            /* we have a scenario */

            artifactMap.set('errors', job.extra['artifact-sim-errors-download']);
          }

          if (typeof job.extra['artifact-sim-log-download'] != 'undefined') {

            /* we have a scenario */

            artifactMap.set('log', job.extra['artifact-sim-log-download']);
          }

          if (typeof job.extra['artifact-movement-download'] != 'undefined') {

            /* we have a scenario */

            artifactMap.set('movement', job.extra['artifact-movement-download']);
          }

          if (typeof job.extra['artifact-kpi-download'] != 'undefined') {

            /* we have a scenario */

            artifactMap.set('kpi', job.extra['artifact-kpi-download']);
          }

          if (typeof job.extra['artifact-kpix-download'] != 'undefined') {

            /* we have a scenario */

            artifactMap.set('kpix', job.extra['artifact-kpix-download']);
          }

          if (!artifactMap.has('scenario') || !artifactMap.has('movement')) {

            iel.unBusy();

            Logger.error(`[InputsElement] importRemoteSimulation - remote job is missing its scenario or its movement output.`);

            const update: TaskSimImportFailed = new TaskSimImportFailed(taskId, `Remote simulation is missing scenario or movement data.`);

            iel._project.services.taskMonitor.progress(update);

            return;
          }

          /* now build out the key map, which is where the Google blobs will reside as our internal blobs */

          let keyMap: Map<string, [string, BlobType]> = new Map<string, [string, BlobType]>;

          if (artifactMap.has('scenario')) {

            keyMap.set('scenario', [storage + '/' + basename(artifactMap.get('scenario')), BlobType.Scenario]);
          }

          if (artifactMap.has('cmd')) {

            keyMap.set('cmd', [resultsFolder + basename(artifactMap.get('cmd')), BlobType.Json]);
          }

          if (artifactMap.has('errors')) {

            keyMap.set('errors', [resultsFolder + basename(artifactMap.get('errors')), BlobType.File]);
          }

          if (artifactMap.has('log')) {

            keyMap.set('log', [resultsFolder + basename(artifactMap.get('log')), BlobType.File]);
          }

          if (artifactMap.has('movement')) {

            keyMap.set('movement', [resultsFolder + basename(artifactMap.get('movement')), BlobType.SimResult]);
          }

          if (artifactMap.has('kpi')) {

            keyMap.set('kpi', [resultsFolder + basename(artifactMap.get('kpi')), BlobType.KPI]);
          }

          if (artifactMap.has('kpix')) {

            keyMap.set('kpix', [resultsFolder + basename(artifactMap.get('kpix')), BlobType.KPIX]);
          }

          Logger.debug(`[InputsElement] importRemoteSimulation - blobs: `, keyMap);

          {
            progress = 2;

            let update: TaskSimImport = new TaskSimImport(taskId, `${keyMap.size} Remote Artifacts found...`, progress);

            iel._project.services.taskMonitor.progress(update);
          }

          /* - - - - 3. batch (forkJoin) the blob imports  - - - - */

          /* ok, we're ready to do the batch import of the blobs, build out a batch of observables... */

          /*
           * Note: forkJoin will fail entirely if even one of the requests fail.  So, to have some graceful
           * recovery, we catch any request errors and fake a result which is clearly an error.
           *
           */

          const observables: any[] = [];
          let   failed: string[]   = [];
          let engineTag: string    = iel._project.services.linesim.engineOfEndpoint(artifactMap.get('scenario'));

          Logger.debug(`[InputsElement] importRemoteSimulation - implied engine: `, engineTag);

          for (let [k,data] of keyMap.entries()) {

            let src: string    = artifactMap.get(k);
            let dst: string    = data[0];
            let kind: BlobType = data[1];

            let obs: Observable<Object> = iel._project.services.blobber.googleImport(
              src,
              dst,
              kind,
              basename(dst),
              'simulation artifact',
              engineTag
            ).pipe(
              catchError((err , obs) => {

                Logger.error(`[InputsElement] importRemoteSimulation - ERROR ${dst}`, err);

                let message = "artifact import request Failed!";

                if (isset(err.statusText)) {

                  message = err.statusText;
                }

                if (isset(err.error.message)) {

                  message = err.error.message;
                }

                failed.push(message);

                return of({key: dst, error: err});
              })
            );

            observables.push(obs);
          }

          /*
           * ok, the batch is ready for forkJoin()...
           *
           */

          let N: number = observables.length;
          let n: number = 0;

          if (N <= 0) {

            iel.unBusy();

            Logger.error(`[InputsElement] importRemoteSimulation - no valid artifacts to import.`);

            const update: TaskSimImportFailed = new TaskSimImportFailed(taskId, `No artifacts to import.`);

            iel._project.services.taskMonitor.progress(update);

            return ;
          }

          {
            progress = 5;

            let update: TaskSimImport = new TaskSimImport(taskId, `Artifact download starting...`, progress);

            iel._project.services.taskMonitor.progress(update);
          }

          forkJoin(observables.map(o => o.pipe(tap((item: any): void => {

            /* each tapped item is the blob result (blob meta) from the individual import request */

            /* as the downloads complete, update the import progress ... */

            n++;

            progress = parseFloat(((n/N)*100.0).toFixed(2));

            /* just ignore the fake results (error items) */

            if (typeof item['error'] != 'undefined') {

              return ;
            }

            let dst: string = item.key;
            let src: string = '';

            for (let [k,data] of keyMap.entries()) {

              if (data[0] == dst) {

                src = artifactMap.get(k);

                break;
              }
            }

            Logger.debug(`[InputsElement] importRemoteSimulation - imported (${progress}): ${src} => ${dst}`);

            {
              let update: TaskSimImport = new TaskSimImport(taskId, `${basename(src)} imported...`, progress);

              iel._project.services.taskMonitor.progress(update);
            }

          })))).subscribe({

            next: (results: any[]): void => {

              iel.unBusy();

              Logger.debug('[InputsElement] importRemoteSimulation - imported blobs: ', results);

              /* - - - - 4. create the subtree nodes as needed in the project manager  - - - - */

              {
                progress = 95;

                let update: TaskSimImport = new TaskSimImport(taskId, `Updating project...`, progress);

                iel._project.services.taskMonitor.progress(update);
              }

              /*
               * map the blob keys to their type, so we know which keys to assign to which
               * child elements.
               *
               */

              let blobMap: Map<string,string> = new Map<string,string>();

              for (let key of results.map(x => x.key)) {

                for (let [k, v] of keyMap.entries()) {

                  if (v[0] == key) {

                    blobMap.set(k, key);

                    break;
                  }
                }
              }

              /* time to build out the subtree ... */

              Logger.debug('[InputsElement] importRemoteSimulation - mapped blobs: ', blobMap);

              let scenarioLabel: string      = basename(job.details.name);

              /* check for duplication by name */

              if (iel._children.map(x => x._label).filter(x => x === scenarioLabel).length != 0) {

                const kids: ProjectElement[] = iel._children;
                let newName: string          = '';
                let n: number                = 1;

                while (n < 50) {

                  newName = scenarioLabel + ` (${n})`;

                  if (iel._children.map(x => x._label).filter(x => x == newName).length === 0) {

                    scenarioLabel = newName;

                    break;
                  }

                  n++;
                }

                if (n >= 50) {

                  /* too many duplicates */

                  Logger.error(`[InputsElement] importRemoteSimulation - too many duplicate names (${scenarioLabel}).`);

                  const update: TaskSimImportFailed = new TaskSimImportFailed(taskId, `Too many duplicates`);

                  iel._project.services.taskMonitor.progress(update);

                  return ;
                }
              }

              /* the scenario */

              Logger.debug(`[InputsElement] importRemoteSimulation - . scenario ...`);

              let sel: BlobElement = ProjectElementFactory.fromObject(
                ProjectElementType.Blob,
                {
                  project: iel._project,
                  id:      '',
                  type:    BlobType.Scenario,
                  key:     blobMap.get('scenario'),
                  parent:  iel,
                  publish: true,
                  label:   scenarioLabel
                }) as BlobElement;

              iel._project.expand([iel]);

              iel.save();

              /* the simulation result child of the scenario */

              Logger.debug(`[InputsElement] importRemoteSimulation - . result ...`);

              job.key            = blobMap.get('scenario') ?? '';
              job.localCmdkey    = blobMap.get('cmd')      ?? '';
              job.localErrorskey = blobMap.get('errors')   ?? '';
              job.localLogkey    = blobMap.get('log')      ?? '';
              job.localOutputKey = blobMap.get('movement') ?? '';
              job.localKPIkey    = blobMap.get('kpi')      ?? '';
              job.localKPIXkey   = blobMap.get('kpix')     ?? '';

              const rel: LineSimJobElement = ProjectElementFactory.fromObject(
                ProjectElementType.LineSimResult,
                {
                  project: sel._project,
                  id:      '',
                  job,
                  parent:  sel,
                  publish: true
                }) as LineSimJobElement;

              iel._project.expand([sel]);

              sel.save();

              /* the reaction time property */

              Logger.debug(`[InputsElement] importRemoteSimulation - . reaction property ...`);

              /*
               * under the result element we hang any properties and the KPIX files
               * if we have them.
               *
               */

              let prt: number = rel._job?.details?.passenger_reaction_time ?? 0;
              let frt: number = rel._job?.details?.freight_reaction_time ?? 0;

              const rt: PropertyElement = ProjectElementFactory.fromObject(
                ProjectElementType.Property,
                {
                  project: rel._project,
                  id:      '',
                  name:    `P: ${(prt / 60).toFixed(1)} mins, F: ${(frt / 60).toFixed(1)} mins`,
                  value:   '',
                  data:    new Map(Object.entries({passenger_reaction_time: prt, freight_reaction_time: frt})),
                  parent:  rel,
                  publish: true,
                  icon:    `reaction-time-32.png`
                }) as PropertyElement;

              rt.save();

              if (rel._job.localKPIXkey) {

                /* the kpix time property */

                Logger.debug(`[InputsElement] importRemoteSimulation - . kpix ...`);

                /*
                 * if the cloud-run based simulation pipeline completes ok, then we have a KPI
                 * and it is downloaded as a blob already, so we can go ahead and create a KPI
                 * child at this point.
                 *
                 */

                const child: KPIXElement = ProjectElementFactory.fromObject(
                  ProjectElementType.KPIX, {
                    project: rel._project,
                    id:      '',
                    input:   rel._job.key,
                    output:  rel._job.localOutputKey,
                    kpix:    rel._job.localKPIXkey,
                    kind:    ProjectElementType.KPIX,
                    parent:  rel,
                    publish: true
                  }) as KPIXElement;

                /* make sure the thing we just created is visible */

                /*
                 *  we're choosing not to expand jobs
                 *
                 lsje._project.expand([lsje]);
                 *
                 */

                child.save();
              }

              {
                progress = 99;

                let update: TaskSimImport = new TaskSimImport(taskId, `Saving project...`, progress);

                iel._project.services.taskMonitor.progress(update);
              }

              /*
               * we have to wait for the redraw and save; the collapse request is published, and
               * can't be processed until JavaScript has control again.
               *
               */

              setTimeout((): void => {

                /* trigger a save */

                Logger.debug(`[InputsElement] importRemoteSimulation - saving project, after job import...`);

                rel._expanded = false;

                rel._project.collapse([rel, ...rel.children()]);

                rel.save();

                Logger.debug(`[InputsElement] importRemoteSimulation - importing completed.`);

                {
                  const update: TaskSimImportComplete = new TaskSimImportComplete(taskId, `Simulation Imported!`);

                  iel._project.services.taskMonitor.progress(update);
                }

              }, 600);

            },

            error: (err): void => {

              iel.unBusy();

              Logger.error(`[InputsElement] importRemoteSimulation - can't import artifacts:`, errorAsText(err));

              const update: TaskSimImportFailed = new TaskSimImportFailed(taskId, `Download Failure: ${errorAsText(err)}`);

              iel._project.services.taskMonitor.progress(update);
            }

          });

        },
        error: (err): void => {

          iel.unBusy();

          Logger.error(`[InputsElement] importRemoteSimulation - could not fetch UUID.`, errorAsText(err));

          const update: TaskSimImportFailed = new TaskSimImportFailed(taskId, `UUID Failure: ${errorAsText(err)}`);

          iel._project.services.taskMonitor.progress(update);
        }

      });

  }

  public doDrop(data, parent: InputsElement) {

    /*
     * check for dropping a simulation job, from one of the remote simulators.  In
     * that case, we have 'job' data, which has been serialized to a string, we just
     * need to recover the SimulationJob, and then do an "import" of the remote job.
     * Basically adding fake simulation subtree, as if we had simulated from the
     * project manager directly.
     *
     * This allows users to bring remote simulations into the project manager without
     * having to download to their desktop and then drag into the project manager.
     *
     */

    let transfer: DataTransfer = data.dataTransfer as DataTransfer;
    let serialized: string     = transfer.getData('job');

    Logger.debug('[InputsElement] doDrop - checking for custom drop: ', serialized);

    if (serialized) {

      /* looks like we have a remote simulation job, try to recover it... */

      let job: SimpleSimJob  = new SimpleSimJob();

      try {

        job.fromJSON(JSON.parse(serialized));

        this.importRemoteSimulation(job);

        return ;

      } catch (err) {

        Logger.error('[InputsElement] doDrop - problems recovering remote simulation job: ', errorAsText(err));

      }

    }

    /*
     * do the default drop behavior (i.e. importing scenarios or scenario/output pairs,
     * or moving elements around
     *
     */

    Logger.debug('[InputsElement] doDrop - doing default drop action...');

    const typeMap: Map<string, string> = new Map<string, string>();

    if (data.files.length) {

      Logger.debug('[InputsElement] doDrop - adding FILE children to ', parent, data.files);

      /*
       * when dropping files into our webapp, we have no idea what the type is, it
       * could be scenario, or plant or ..., so we default to File, but if they used
       * file extensions, then we cast to whatever type we can guess from that.
       *
       */

      const files: File[] = Array.from(data.files);

      Logger.debug('[InputsElement] doDrop - implied type guessing... ');

      for (const f of files) {

        const gt: BlobType =  Blob.guessType(BlobType.File, f.name, '');

        typeMap.set(f.name, Blob.stringOfType(gt));

      }

      Logger.debug('[InputsElement] doDrop - creating samplers... ');

      /*
       * content aware checking; if it's a .json file, and it starts with:
       *
       * {
       *    "scenario": {
       *       "links": {
       *           "link": {
       *
       * Then we can be very sure that the type is "scenario".
       *
       * This is a content aware check though, we have to actually read the first few
       * bytes of the file and do some pattern matching.  But to do any of that, you
       * have to do Blob reading...which is asynchronous.  So we would have to
       * delay the uploading until after we've done the asynchronous fetching.
       *
       * We need to launch async code for each file to do content fetch, and then wait
       * for the "forkJoin()" on all of them.  Having established the sample content for
       * all the files, we cna then just loop through those samples and try to
       * do content aware typing (we're back in synchronous land at that point).
       *
       * tutorials on using forkJoin:
       *
       * @link https://www.agiratech.com/rxjs-error-handling-with-forkjoin/
       * @link https://medium.com/@swarnakishore/performing-multiple-http-requests-in-angular-4-5-with-forkjoin-74f3ac166d61
       *
       */

      const observables: Map<Observable<Object>, File> = new Map<Observable<Object>, File>();
      const readLimit: number                          = 1024;
      const sampleMap: Map<File, string>               = new Map<File, string>();

      for (const f of files) {

        /* map asynchronous file reading to an observer */

        let obs: Observable<Object> = new Observable((sub: Subscriber<string>): void => {

          Logger.debug(`[InputsElement] doDrop - . . new observer: ${f.name}: `);

          const fileReader: FileReader = new FileReader();

          /* when the read is completed */

          fileReader.onload = (ev: ProgressEvent<FileReader>): void => {

            let binary: string    = "";
            let bytes: Uint8Array = new Uint8Array((<any>ev.target).result);

            for (let i = 0; i < bytes.length; i++) {

              binary += String.fromCharCode(bytes[i]);
            }

            /* save our own mapping of the sample data */

            sampleMap.set(f, binary);

            Logger.debug(`[InputsElement] doDrop - . sample [${ev.type}] ${f.name}: `, binary.substr(0, 64));

            sub.next(binary);
            sub.complete();
          }

          /* when the read fails */

          fileReader.onerror = (error: ProgressEvent<FileReader>): void => {

            sub.error(error);
          };

          /* trigger the file reading (asynchronously) ... */

          fileReader.readAsArrayBuffer(f.slice(0, readLimit));

        });

        /*
         * queue up this observable to use in a forkJoin() so we can
         * wait on all of them at once.
         *
         */

        observables.set(obs, f);

      }

      Logger.debug(`[InputsElement] doDrop - content sampling ${observables.size}... `);

      this.busy();

      forkJoin(...observables.keys()).subscribe({

        next: (results): void => {

          this.unBusy();

          /*
           * run through the samples and try to find a typing from the content, instead
           * of just the file extension...
           *
           */

          for (const [f, sample] of sampleMap) {

            /* guess the type based on content... */

            let override: string | null = ContentAwareTyping.typeByContent(sample);

            if (!override) {

              /* can't match */

              continue;
            }

            /* use the content typing instead of file extension typing */

            Logger.debug(`[InputsElement] doDrop - . . overriding ${f.name} with type: ${override}`);

            typeMap.set(f.name, override);
          }

          /*
           * now that we have done any typing overrides in typeMap, do the uploading
           * as usual...
           *
           */

          Logger.debug(`[InputsElement] doDrop - proceeding to upload...`);

          parent.upload(BlobType.File, files, typeMap);

        },
        error: (err): void => {

          this.unBusy();

          if (err.name) {

            if (err.name === 'QuotaExceededError') {

              Logger.error('[InputsElement] doDrop - problems sampling blobs; localstorage is full??', err);
            }
          }

          Logger.error('[InputsElement] doDrop - problems sampling blobs: ', errorAsText(err));

        }

      });

      return true;
    }

    if (!data.dataTransfer.types.find(x => x === 'application/json')) {

      Logger.error('[InputsElement] doDrop - can not drop, no JSON type for drop data: ', data.dataTransfer.types);

      return false;
    }

    const json: any = JSON.parse(data.dataTransfer.getData('application/json'));

    Logger.debug('[InputsElement] doDrop - adding JSON children to ', parent, json);

    /*
     * as above, but when cloning we just want to allow the original type
     * to be used (the thing we are cloning already has a type!)
     *
     */

    const keys: string[] = [];

    json.forEach((item: any): void => {

      keys.push(item.key);

      typeMap.set(item.key, item.type);
    });

    parent.clone(BlobType.File, keys, typeMap);

    Logger.debug('[InputsElement] doDrop - cloning completed.');

    return true;
  }

  /** test if this element accepts the given action */
  public acceptsAction(key: string): boolean {

    return !!this.actionKeys.find(x => x === key);
  }

  /** sub-classes must implement their own actions */
  public action(key: string, selection: ProjectElement[]) {

    switch (key) {

      case 'generatekpitable': {

        this._project.generateKPITable(this);
      }
      break;

      case 'dimensioninput': {

        /* go through a dialog to get the name/type for dimension */

        this._project.services.modals.open(
          'new-dimension-modal',
          (data, resolve, reject) => {

            const name: string   = data.name as string;
            const type: BlobType = data.type as BlobType;

            Logger.debug('[InputsElement] validating: ', name, Blob.stringOfType(type));

            /* is it a valid name? */

            if (name.trim() === '') {

              reject('Dimension name can not be empty.');

              return ;
            }

            /* do we already have an simulation of this name? */

            const peers: ProjectElement[] = this._children;

            for (const peer of peers) {

              if (peer._label === name) {

                reject('Dimension of that name already exists in this simulation.');

                return ;
              }

            }

            /* if we have dimensions, they have to be different types */

            for (const peer of peers) {

              if (peer._type === ProjectElementType.Dimension) {

                if (peer.blobType() === type) {

                  reject('Dimension of that kind already exists in this input group.');

                  return ;
                }
              }
            }

            /* its golden! */

            resolve({name, type});

          },
          (data: any) => {

            const name: string   = data.name as string;
            const type: BlobType = data.type as BlobType;

            /* add the new child simulation */

            Logger.debug('[InputsElement] new dimension: ',  name, type);

            ProjectElementFactory.fromObject(
              ProjectElementType.Dimension, {
                project: this._project,
                id:      '',
                name,
                kind:    type,
                parent:  this,
                publish: true
              });

            /* make sure the thing we just created is visible */

            this._project.expand([this]);

            /* make sure the project is saved */

            this.save();

          });


        break;
      }

      case 'newemptyinput': {

        /* go through a dialog to get the blob name and type */

        this._project.services.modals.open(
          'new-empty-modal',
          (data, resolve, reject) => {

            const name: string   = data.name as string;
            const type: BlobType = data.type as BlobType;

            Logger.debug('[InputsElement] validating: ', name, Blob.stringOfType(type));

            /* is it a valid name? */

            if (name.trim() === '') {

              reject('Blob name can not be empty.');

              return ;
            }

            /* do we already have an simulation of this name? */

            const peers: ProjectElement[] = this._children;

            for (const peer of peers) {

              if (peer._label === name) {

                reject('Blob of that name already exists in this input grouping.');

                return ;
              }

            }

            /* its golden! */

            resolve({name, type});

          },
          (data: any) => {

            const name: string   = data.name as string;
            const type: BlobType = data.type as BlobType;

            /*
             * the blob is stored in the project folder, and from there we make it relative
             * to this node in the project
             *
             */

            const      blobData: string           = '{json: "empty"}';

            const       parents: ProjectElement[] = this.ancestors().reverse();
            parents.shift();
            const       storage: string           = this._project.dirname() +
              '/' +
              parents.map(x => x._label).join('/') + '/Inputs';

            const       storageKey: string        = storage + '/' +
              name +
              Blob.extensionOfType(type);

            Logger.debug('[InputsElement] new (empty) blob: ',  storage, type);

            this._project.services.blobber.create(storageKey, type, blobData, name, 'input element')
              .pipe(first())
              .subscribe(
                () => {

                  /* link the blob to this element */

                  Logger.debug('[InputsElement] new (empty) blob saved: ', storage);

                  ProjectElementFactory.fromObject(
                    ProjectElementType.Blob,
                    {
                      project: this._project,
                      id:      '',
                      type,
                      key:     storage,
                      parent:  this,
                      publish: true
                    });

                  /* make sure the project is saved */

                  this.save();

                });

          }, {
            placeholder: 'Empty Blob Name'
          });


        break;
      }

      case 'addblobinput': {

        /* go through a dialog to get the name/type for dimension */

        this._project.services.modals.open(
          'add-blob-modal',
          (data, resolve, reject) => {

            Logger.debug('[InputsElement] validating (clone) blob(s): ', data);

            const keys: string[]  = data.keys as string[];
            const type: BlobType = data.type as BlobType;

            Logger.debug('[InputsElement] validating (upload): ', keys, Blob.stringOfType(type));

            /* is it a valid name? */

            if (keys.length === 0) {

              reject('Must clone at least one file.');

              return ;
            }

            /* do we already have items of the same name? */

            const peers: ProjectElement[] = this._children;

            for (const peer of peers) {

              for (const f of keys) {

                const base: string = f.substring(f.lastIndexOf('/') + 1);

                if (peer._label === base) {

                  reject(`File '${base}' is already added to this input group.`);

                  return ;
                }

              }

            }

            /* its golden! */

            resolve({keys, type});

            /* its golden! */

            resolve(data);

          },
          (data: any) => {

            /* clone the blobs */

            const keys: string[] = data.keys as string[];
            const type: BlobType = data.type as BlobType;

            Logger.debug('[InputsElement] CLONING: ', keys, Blob.stringOfType(type));

            /* for each file we create a blob of the given type... */

            this.clone(type, keys);

          });


        break;
      }

      case 'adduploadinput': {

        if (this.pending.length !== 0) {

          /* we're already uploading something */

          return ;
        }

        /* go through a dialog to get blob type and files to upload */

        this._project.services.modals.open(
          'add-upload-modal',
          (data, resolve, reject) => {

            const files: File[]  = data.files as File[];
            const type: BlobType = data.type as BlobType;

            Logger.debug('[InputsElement] validating (upload): ', files, Blob.stringOfType(type));

            /* is it a valid name? */

            if (files.length === 0) {

              reject('Must upload at least one file.');

              return ;
            }

            /* do we already have items of the same name? */

            const peers: ProjectElement[] = this._children;

            for (const peer of peers) {

              for (const f of files) {

                if (peer._label === f.name) {

                  reject(`File '${f.name}' is already added to this input group.`);

                  return ;
                }

              }

            }

            /* its golden! */

            resolve({files, type});

          },
          (data: any) => {

            const files:  File[] = data.files as File[];
            const type: BlobType = data.type as BlobType;

            Logger.debug('[InputsElement] ADDING: ', files, Blob.stringOfType(type));

            /* for each file we create a blob of the given type... */

            this.upload(type, files);

          });


        break;
      }
    }
  }

  /** helper to copy existing blobs into our project */
  protected clone(type: BlobType, keys: string[], typeMap?: Map<string, string>) {

    const ie: InputsElement = this;

    /*
     * we use the fork & join pattern to get all the files created as blobs in parallel
     * and then at the end, we actually add them to the project as blob elements.
     *
     * tutorials on using forkJoin:
     *
     * @link https://www.agiratech.com/rxjs-error-handling-with-forkjoin/
     * @link https://medium.com/@swarnakishore/performing-multiple-http-requests-in-angular-4-5-with-forkjoin-74f3ac166d61
     *
     */

    Logger.debug('[InputsElement] cloning blobs...');

    keys.forEach((item) => {

      this.pendingKeys.push([item, 'latest']);
    });

    /*
     * before creating anything, rename the files if needed; if we have name conflicts, append a
     * number to make the files unique.
     *
     */

    const       kids: ProjectElement[]    = ie._children;
    const keyNameMap: Map<string, string> = new Map<string,string>();

    this.pendingKeys.forEach((pair: [string, string]) => {

      let    label: string = pair[0];
      let altLabel: string = '';

      if (label.lastIndexOf('/') > 0) {

        label = label.substring(label.lastIndexOf('/') + 1);
      }

      altLabel = label;

      if (altLabel.lastIndexOf('.') !== -1) {

        altLabel = altLabel.substring(0, altLabel.lastIndexOf('.'));
      }

      if (kids.map(x => x._label).filter(x => x === altLabel).length === 0) {

        /* no clash of name */

        keyNameMap.set(pair[0], altLabel);

        return ;
      }

      /* try to find a none-conflicting name */

      let newName: string = '';
      let       n: number = 2;

      while (n < 50) {

        newName = altLabel + ` (${n})`;

        if (kids.map(x => x._label).filter(x => x === newName).length === 0) {

          /* no clash of name */

          keyNameMap.set(pair[0], newName);

          return ;
        }

        n++;
      }

      /* we've exhausted the slots for trying to make unique, just ignore this one */

    });

    /* what folder are they going in? */

    const       parents: ProjectElement[] = this.ancestors().reverse();
    parents.shift();
    let         storage: string           = this._project.dirname() +
      '/' +
      parents.map(x => x._label).join('/') + '/Inputs';

    if (this._elementKind === 'Dimension') {

      storage  = ie._project.dirname() +
        '/' +
        parents.map(x => x._label).join('/') + `/${this._label}`;
    }

    /* create the observables for copying blobs... */

    const observables: any[] = [];

    for (const f of this.pendingKeys) {

      if (!keyNameMap.has(f[0])) {

        /* was never mapped to a child name */

        continue ;
      }

      /* file them in the project's working folder */

      let   b0: string = f[0];
      let base: string = keyNameMap.get(f[0]);

      if (b0.lastIndexOf('/')) {

        b0 = b0.substring(b0.lastIndexOf('/') + 1);
      }

      if (f[0].lastIndexOf('.')) {

        base += f[0].substring(f[0].lastIndexOf('.'));
      }

      const path     = f[0];
      const dest     = storage + '/' + base;
      const instance = f[1];
      const name     = base;

      let blobType: BlobType = type;

      if (typeMap) {

        if (typeMap.has(f[0])) {

          blobType = Blob.typeofString(typeMap.get(f[0]));

        } else if (typeMap.has(b0)) {

          blobType = Blob.typeofString(typeMap.get(b0));
        }
      }

      /* as we copy we are also forcing the type to be whatever the user selected as the import type */

      observables.push( this._project.services.blobber.copy(path, dest, instance, name, Blob.stringOfType(blobType)).pipe(first()) );

    }

    /* now fork & join to do them all in parallel */

    this.busy();

    forkJoin(observables).subscribe(
      (results) => {

        this.unBusy();

        /* everything got created ok, add the children in the project and save the latest project */

        Logger.debug('[InputsElement] blobs cloned: ', results);

        for (const r of results) {

          const blob: Blob = r as Blob;

          /*
           * note that we don't try to add children all at once; the presentation (i.e. FancyTree)
           * has to take them one at a time anyway...so it can insert them in the right order.
           *
           */

          ProjectElementFactory.fromObject(
            ProjectElementType.Blob,
            {
              project: this._project,
              id:      '',
              type:    blob.type,
              key:     blob.key,
              parent:  this,
              publish: true
            });
        }

        /* make sure the thing we just created is visible */

        this._project.expand([this]);

        /* make sure the project is saved */

        this.pendingKeys = [];

        this.save();

      },
      (error) => {

        this.unBusy();

        if (error.name) {

          if (error.name === 'QuotaExceededError') {

            Logger.error('[InputsElement] problems cloning blobs; localstorage is full??', error);
          }
        }

        Logger.error('[InputsElement] problems cloning blobs: ', error);

      });

  }

  /** helper to create a fake result when they drop a scenario and a movement */
  protected fakeResult(files: File[], typeMap?: Map<string, string>) {

    Logger.debug(`[InputsElement] fakeResult - files=${files}, typeMap:`, typeMap);

    /*
     * to fake a result from a scenario and train movement that they have dropped into
     * the project manager, there are several steps:
     *
     *   1. Create a scenario blob element
     *   2. Create a movement (output) blob
     *   3. Create a fake job child element, and configure it to have the train movement blob
     *   4. Ensure the project is saved, and expand the children so they are immediately visible.
     *
     */

    /* -=-=: Step 1 - create the child for the scenario element */

    /* first figure out which file is which ... */

    let scenario: File|null = null;
    let movement: File|null = null;

    for (let f of files) {

      let t: string = typeMap.get(f.name);

      if (Blob.typeofString(t) == BlobType.Scenario) {

        scenario = f;

        continue;
      }

      if (Blob.typeofString(t) == BlobType.SimResult) {

        movement = f;
      }
    }

    if ((scenario == null) || (movement == null)) {

      Logger.error(`[InputsElement] fakeResult - could not map scenario and output.`);

      return ;
    }

    Logger.debug(`[InputsElement] fakeResult - scenario=${scenario}, movement=${movement}`);

    let iel: InputsElement = this;

    iel.busy();

    Logger.debug(`[InputsElement] fakeResult - generating uuid ...`);

    iel._project.services.uuid.generate()
      .pipe(first())
      .subscribe({
        next: (uuidData: any): void => {

          const            uuid: string = uuidData.guid;
          const   projectFolder: string = iel._project.dirname();
          const   resultsFolder: string = projectFolder + `/fake-results/${uuid}/`;
          const    scenarioName: string = iel.basename(scenario.name) + '.scn';
          const fakeScenarioKey: string = resultsFolder + scenarioName;
          const        uploadId: string = TaskUpdate.generateId();

          Logger.debug(`[InputsElement] fakeResult - creating fake scenario ${fakeScenarioKey} ...`);

          iel._project.services.blobber.create(fakeScenarioKey, BlobType.Scenario, scenario, iel.basename(scenario.name) + '.scn', 'fake scenario')
            .pipe(first())
            .subscribe({
              next: (scenarioResult: any): void => {

                /* link the scenario  to its parent */

                Logger.debug('[InputsElement] fakeResult - fake scenario saved: ', scenarioResult);

                let scenarioElement: BlobElement = ProjectElementFactory.fromObject(
                  ProjectElementType.Blob,
                  {
                    project: iel._project,
                    id:      '',
                    type:    BlobType.Scenario,
                    key:     fakeScenarioKey,
                    parent:  iel,
                    publish: true
                  }) as BlobElement;

                iel._project.expand([iel]);

                /* -=-=: Step 2 - create the blob for the train movement result */

                const    movementName: string = iel.basename(movement.name) + '.res';
                const fakeMovementKey: string = resultsFolder + movementName;

                Logger.debug(`[InputsElement] fakeResult - creating fake movement ${fakeMovementKey} ...`);

                iel._project.services.blobber.create(fakeMovementKey, BlobType.SimResult, movement, iel.basename(movement.name) + '.scn', 'fake movement')
                  .pipe(first())
                  .subscribe({
                    next: (movementResult: any): void => {

                      Logger.debug('[InputsElement] fakeResult - fake movement saved: ', movementResult);

                      /* -=-=: Step 3 - extra any metadata that we can from the movement file */

                      Logger.debug(`[InputsElement] fakeResult - extracting meta data from movement file...`);

                      new MovementMetaBuilder()
                        .setChunkSize(256 * 1024)
                        .setFile(movement)
                        .setLoader(new JSONLoader().onProgress((event: LoadingProgressEvent): void => {

                          Logger.debug(`[InputsElement] fakeResult - progress: [${event.progress}] - ${event.summary}`);

                        }))
                        .build()
                        .then(
                          (meta: {job_detail: any, extra: any}): void => {

                            Logger.debug(`[InputsElement] fakeResult - meta data found: job_detail: `, meta.job_detail);
                            Logger.debug(`[InputsElement] fakeResult - meta data found: extra: `,      meta.extra);

                            meta.extra['job_detail'] = meta.job_detail;

                            /* -=-=: Step 4 - create the fake job element */

                            /*
                             * Note: because we have job_detail and extra from the metadata of the movement
                             * file, we can try to improve the quality of the metadata tied to the job.  Its
                             * not possible to have full metadata, since this isn't an actual job, we're just
                             * faking out as much as we can.
                             *
                             */

                            let prt: number     = 0;
                            let frt: number     = 0;
                            let cd: string      = '';
                            let ct: string      = '';
                            let version: string = '';
                            let cid: number     = 0;

                            if (typeof meta?.job_detail?.reaction_time == 'object') {

                              /* try to pull out the reaction times... */

                              if (typeof meta.job_detail.reaction_time['passenger_mins'] != 'undefined') {

                                prt = meta.job_detail.reaction_time['passenger_mins'] * 60;
                              }

                              if (typeof meta.job_detail.reaction_time['passenger_reaction_time'] != 'undefined') {

                                /* the actual line simulator outputs the reaction time in hours */

                                prt = meta.job_detail.reaction_time['passenger_reaction_time'] * 3600;
                              }

                              if (typeof meta.job_detail.reaction_time['non_passenger_mins'] != 'undefined') {

                                frt = meta.job_detail.reaction_time['non_passenger_mins'] * 60;
                              }

                              if (typeof meta.job_detail.reaction_time['freight_reaction_time'] != 'undefined') {

                                /* the actual line simulator outputs the reaction time in hours */

                                frt = meta.job_detail.reaction_time['freight_reaction_time'] * 3600;
                              }

                              if (typeof meta.job_detail['Version'] != 'undefined') {

                                version = meta.job_detail['Version'];
                              }

                              if (typeof meta.job_detail['Compile Time'] != 'undefined') {

                                ct = meta.job_detail['Compile Time'];
                              }

                              if (typeof meta.job_detail['Compile Date'] != 'undefined') {

                                cd = meta.job_detail['Compile Date'];
                              }

                              if (typeof meta.job_detail['client_id'] != 'undefined') {

                                cid = meta.job_detail['client_id'];
                              }
                            }

                            let details: RemoteSimJob = new RemoteSimJob({
                              id:                     0,
                              jid:                    0,
                              scenario_id:            meta?.job_detail?.scenario_id  ?? 0,
                              pid:                    meta?.job_detail?.PID          ?? 0,
                              client_id:              cid,
                              lasterror:              meta?.job_detail?.last_error   ?? '',
                              author:                 meta?.job_detail?.author       ?? '',
                              name:                   meta?.extra?.name              ?? '',
                              extra:                  meta?.extra                    ?? {},
                              dequeue_at:             meta?.job_detail?.dequeued     ?? (new Date()).toISOString(),
                              created_at:             meta?.job_detail?.created      ?? (new Date()).toISOString(),
                              updated_at:             meta?.job_detail?.updated      ?? (new Date()).toISOString(),
                              importfile:             fakeScenarioKey,
                              input:                  fakeScenarioKey,
                              log:                    '',
                              output:                 fakeMovementKey,
                              priority:               '1',
                              progress:               '100',
                              progress_out:            (meta?.job_detail?.simulator_output as string)?.split('\n') ?? [],
                              options:                 JSON.parse(meta?.job_detail?.options ?? ''),
                              runtime:                 meta?.job_detail?.milliseconds?.simulation / 1000.0,
                              totaltime:               meta?.job_detail?.milliseconds?.total / 1000.0,
                              waittime:                0,
                              passenger_reaction_time: prt,
                              freight_reaction_time:   frt,
                              version,
                              compile_date: cd,
                              compile_time: ct
                            });

                            details.status = SimulationJob.statusType('success');

                            const config: SimConfig = new SimConfig();
                            const job: SimpleSimJob = new SimpleSimJob({
                              type:     SimulationJobType.SIMPLE_SIM,
                              jobId:    0,
                              jobUuid:  uuid,
                              status:   SimulationStatusType.SUCCESS,
                              key:      fakeScenarioKey,
                              instance: 'latest',
                              config,
                              extra:    meta?.extra ?? {},
                              created:  fromMySQLDate(meta?.job_detail?.created)  ?? new Date(),
                              dequeue:  fromMySQLDate(meta?.job_detail?.dequeued) ?? new Date(),
                              updated:  fromMySQLDate(meta?.job_detail?.updated)  ?? new Date(),
                              details
                            });

                            job.resolved       = true;
                            job.progress       = 100.0;
                            job.localOutputKey = fakeMovementKey;
                            job.localLogkey    = '';

                            // noinspection JSUnusedLocalSymbols
                            const lsje: LineSimJobElement = ProjectElementFactory.fromObject(
                              ProjectElementType.LineSimResult,
                              {
                                project: iel._project,
                                id:      '',
                                job,
                                parent:  scenarioElement,
                                publish: true
                              }) as LineSimJobElement;

                            /* -=-=: Step 5 - expand the children, and save the project */

                            /* make sure the thing we just created is visible */

                            iel._project.expand([scenarioElement]);

                            /* -=-=: Step 6 - add metadata children (optional) */

                            /*
                             * if we have metadata like passenger train reaction time, we need to
                             * make that visible; so we add "property" nodes as children to the job.
                             * We can use that mechanic to show any other interesting properties as
                             * well.
                             *
                             */

                            /* for now, the only one we're adding is reaction time */

                            const rt: PropertyElement = ProjectElementFactory.fromObject(
                              ProjectElementType.Property,
                              {
                                project: iel._project,
                                id:      '',
                                name:    `P: ${(prt / 60).toFixed(1)} mins, F: ${(frt / 60).toFixed(1)} mins`,
                                value:   '',
                                data:    new Map(Object.entries({passenger_reaction_time: prt, freight_reaction_time: frt})),
                                parent:  lsje,
                                publish: true,
                                icon:    `reaction-time-32.png`
                              }) as PropertyElement;

                            /* make sure the project is saved */

                            /*
                             * we're choosing not to expand jobs
                             *
                            lsje._project.expand([rt]);
                             *
                             *
                             */

                            lsje._expanded = false;

                            lsje._project.collapse([lsje, ...lsje.children()]);

                            iel.save();

                            iel.unBusy();

                            Logger.debug(`[InputsElement] fakeResult - created!`);

                          });

                    },

                    error: (err: any): void => {

                      iel.unBusy();

                      Logger.error(`[InputsElement] fakeResult - could not create fake movement blob.`, errorAsText(err));
                    }

                  });

              },
              error: (err): void => {

                iel.unBusy();

                Logger.error(`[InputsElement] fakeResult - could not fake create scenario blob.`, errorAsText(err));
              }

            });

        },
        error: (err): void => {

          iel.unBusy();

          Logger.error(`[InputsElement] fakeResult - could not generate uuid for creating fake result key.`, errorAsText(err));
        }

      });

  }

  /** helper to do the actual upload of files */
  protected upload(type: BlobType, files: File[], typeMap?: Map<string, string>) {

    Logger.debug(`[InputsElement] upload - type=${type}, files=${files}, typeMap:`, typeMap);

    /* pre-flight check, no file can be bigger than our backend file upload max size (currently 512MB) */

    let tooBig: File          = null;
    const   ie: InputsElement = this;

    Logger.debug(`[InputsElement] upload - checking if files too big...`);

    files.forEach((f: File) => {

      if (tooBig) {

        return ;
      }

      if (f.size >= (1000 * 1000 * 512)) {

        tooBig = f;
      }
    });

    if (tooBig) {

      ie._project.services.modals.open('error-msg-modal',
        () => {
        },
        () => {
        },
        {
          message: `${tooBig.name} is too big (${bytesToSize(tooBig.size)} > 512Mb) to upload.`
        });

      /* can not continue */

      return ;
    }

    /*
     * if they gave us a pair of files, and one is a scenario and the other is simulation
     * result, then they are trying to create a fake simulation result.  In that exact
     * case, we don't do batch upload of inputs; instead we create a fake result :)
     *
     */

    Logger.debug(`[InputsElement] upload - checking if fake result being requested...`);

    if ((files.length == 2) && (typeMap.size == 2)) {

      let hasScenario: boolean = false;
      let hasMovement: boolean = false;

      for (let v of typeMap.values()) {

        if (Blob.typeofString(v) == BlobType.Scenario) {

          hasScenario = true;
        }

        if (Blob.typeofString(v) == BlobType.SimResult) {

          hasMovement = true;
        }
      }

      Logger.debug(`[InputsElement] upload - hasScenario=${hasScenario}, hasMovement=${hasMovement}`);

      /* are we creating a fake result? */

      if (hasScenario && hasMovement) {

        ie.fakeResult(files, typeMap);

        return;
      }
    }

    /*
     * batch upload scenario inputs ...
     *
     */

    /*
     * before creating anything, rename the files if needed; if we have name conflicts, append a
     * number to make the files unique.
     *
     */

    const kids: ProjectElement[] = ie._children;

    files.forEach((f: File) => {

      const    label: string = f.name;
      let   altLabel: string = label;

      if (altLabel.lastIndexOf('.') !== -1) {

        altLabel = altLabel.substring(0, altLabel.lastIndexOf('.'));
      }

      if (kids.map(x => x._label).filter(x => x === altLabel).length === 0) {

        /* no clash of name */

        ie.pending.push([f, altLabel]);

        return ;
      }

      /* try to find a none-conflicting name */

      let newName: string = '';
      let       n: number = 2;

      while (n < 50) {

        newName = altLabel + ` (${n})`;

        if (kids.map(x => x._label).filter(x => x === newName).length === 0) {

          /* no clash of name */

          ie.pending.push([f, newName]);

          return ;
        }

        n++;
      }

      /* we've exhausted the slots for trying to make unique, just ignore this one */

    });

    /*
     * we use the fork & join pattern to get all the files created as blobs in parallel
     * and then at the end, we actually add them to the project as blob elements.
     *
     * tutorials on using forkJoin:
     *
     * @link https://www.agiratech.com/rxjs-error-handling-with-forkjoin/
     * @link https://medium.com/@swarnakishore/performing-multiple-http-requests-in-angular-4-5-with-forkjoin-74f3ac166d61
     *
     */

    Logger.debug('[InputsElement] upload - creating blobs: ', ie.pending);

    /* what folder are they going in? */

    const       parents: ProjectElement[] = ie.ancestors().reverse();
    parents.shift();
    let         storage: string           = ie._project.dirname() +
      '/' +
      parents.map(x => x._label).join('/') + '/Inputs';

    if (this._elementKind === 'Dimension') {

      storage  = ie._project.dirname() +
        '/' +
        parents.map(x => x._label).join('/') + `/${this._label}`;

    }

    /*
     * on doing Angular requests with progress:
     *
     *   https://nils-mehlhorn.de/posts/angular-file-download-progress
     *   https://angular.io/api/common/http/HttpProgressEvent
     */

    /* create the observables for creating blobs... */

    const observables: any[] = [];
    const tracker: Map<Observable<Object>, {key: string, id: string, total: number, type: BlobType, icon: string}> =
      new Map<Observable<Object>, {key: string, id: string, total: number, type: BlobType, icon: string}>();

    for (const pair of ie.pending) {

      let childName: string = pair[1];

      if (pair[0].name.lastIndexOf('.')) {

        childName += pair[0].name.substring(pair[0].name.lastIndexOf('.'));
      }

      const key: string = storage + '/' + childName;

      let blobType: BlobType = type;

      if (typeMap) {

        if (typeMap.has(key)) {

          blobType = Blob.typeofString(typeMap.get(key));

        } else if (typeMap.has(basename(childName))) {

          blobType = Blob.typeofString(typeMap.get(basename(childName)));

        } else if (typeMap.has(pair[0].name)) {

          blobType = Blob.typeofString(typeMap.get(pair[0].name));
        }
      }

      let o = ie._project.services.blobber.create(
        key,
        blobType,
        pair[0],
        childName,
        'uploaded blob',
        false,
        true).pipe(

          catchError((err , obs) => {

          let stream: {key: string, id: string, total: number, type: BlobType, icon: string} = tracker.get(obs);

          Logger.error(`[InputsElement] upload - ERROR ${stream.key}`, err);

          let message = "Upload Failed!";

          if (isset(err.statusText)) {

            message = err.statusText;
          }

          if (isset(err.error.message)) {

            message = err.error.message;
          }

          let update  = new TaskUpdateFileUploadFailed(stream.id, stream.key, stream.icon, message);

          Logger.debug(`[InputsElement] ] upload - progress: ${tracker.get(o)} uploaded FAILED`, update);

          ie._project.services.taskMonitor.progress(update);

          return of(null);
        })
      );

      observables.push(o);

      tracker.set(o, {
        key,
        id:    TaskUpdate.generateId(),
        total: pair[0].size,
        type:  blobType,
        icon:  FileBlob.iconOfType(blobType)
      });
    }

    Logger.debug('[InputsElement] upload - observables created, forking...');

    /* now fork & join to do them all in parallel */

    this.busy();

    forkJoin(observables.map(
      o => o.pipe(

        tap((e: HttpEvent<any>) => {

          if (!e) {

            return ;
          }

          if (e.type == HttpEventType.UploadProgress) {

            /* upload progress */

            let stream: {key: string, id: string, total: number, type: BlobType, icon: string} = tracker.get(o);

            let update = new TaskUpdateFileUpload(stream.id, stream.key, stream.icon, e.loaded, e.total);

            Logger.debug(`[InputsElement] upload - progress: ${stream.key} `, update);

            ie._project.services.taskMonitor.progress(update);

          } else if (e.type == HttpEventType.Sent) {

            /* the request was sent */

          }  else if (e.type == HttpEventType.ResponseHeader) {

            /* we got a response back, this means the upload was completed. */

            let stream: {key: string, id: string, total: number, type: BlobType, icon: string} = tracker.get(o);

            let update = new TaskUpdateFileUploadComplete(stream.id, stream.key, stream.icon, stream.total);

            Logger.debug(`[InputsElement] upload - progress: ${tracker.get(o)} uploaded COMPLETE`, update);

            ie._project.services.taskMonitor.progress(update);

          }

        })

      ))).subscribe({

        next: (results: any): void => {

          ie.unBusy();

          /* everything got created ok, add the children in the project and save the latest project */

          Logger.debug('[InputsElement] upload - blobs updated: ', results);

          for (const r of results) {

            const blob: Blob = r.body as Blob;

            /*
             * note that we don't try to add children all at once; the presentation (i.e. FancyTree)
             * has to take them one at a time anyway...so it can insert them in the right order.
             *
             */

            ProjectElementFactory.fromObject(
              ProjectElementType.Blob,
              {
                project: ie._project,
                id: '',
                type: blob.type,
                key: blob.key,
                parent: ie,
                publish: true
              });

          }

          /* make sure the thing we just created is visible */

          ie._project.expand([ie]);

          /* make sure the project is saved */

          ie.pending = [];

          ie.save();

        },

        error: (err): void => {

          ie.unBusy();

          if (!err) {

            return;
          }

          if (err.name) {

            if (err.name === 'QuotaExceededError') {

              Logger.error('[InputsElement] upload - problems creating blobs; localstorage is full??', err);
            }
          }

          Logger.error('[InputsElement] upload - problems creating blobs: ', errorAsText(err));

        }
    });

  }

}
